/**
 * The profile's "Documents & records" tab: a person's scans (KTP, KK, NPWP, BPJS cards,
 * bank book), HR's records with their files, and, for HR, the sign-in.
 * Files open in a new tab; the API checks who may open them and audits each opening.
 */
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { PERSON_DOCUMENTS, type PersonDocumentKind } from '../lib/documents.ts';
import { date, relative } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';
import { Icon } from './Icon.tsx';
import { AccountDialog, DocumentDialog, RecordDialog, useInvite, useRemove, useUnlock, type RecordRef } from './PersonEdit.tsx';
import { SetupLinkDialog } from './GxMail.tsx';
import { Button, Chip, Panel } from './ui.tsx';

const openLink = (href: string) => (
  <a className="btn btn--sm btn--ghost" href={href} target="_blank" rel="noopener">
    <Icon name="file" />
    Open
  </a>
);

export function DocumentsPanel({ id, documents, hasBank, canOpen, canUpload }: { id: string; documents: Record<PersonDocumentKind, boolean>; hasBank: boolean; canOpen: boolean; canUpload: boolean }) {
  const [uploading, setUploading] = useState<PersonDocumentKind | null>(null);
  const onFile = PERSON_DOCUMENTS.filter((d) => documents[d.kind]).length;
  return (
    <Panel eyebrow={`Restricted · ${onFile} of ${PERSON_DOCUMENTS.length} on file`} title="Document scans">
      <ul className="list-plain">
        {PERSON_DOCUMENTS.map((d) => (
          <li key={d.kind} className="mini-row">
            <Icon name="file" size={16} style={{ flex: 'none', color: 'var(--ink-4)' }} />
            <span>{d.label}</span>
            {documents[d.kind] ? <Chip tone="ok">On file</Chip> : <span className="faint">Missing</span>}
            <span className="mini-row__aside row" style={{ gap: 4 }}>
              {documents[d.kind] && canOpen ? openLink(`/api/people/${id}/documents/${d.kind}`) : null}
              {canUpload ? (
                <Button size="sm" variant="ghost" icon={documents[d.kind] ? 'refresh' : 'plus'} disabled={d.kind === 'bank_book' && !hasBank} title={d.kind === 'bank_book' && !hasBank ? 'Add the bank account first' : undefined} onClick={() => setUploading(d.kind)}>
                  {documents[d.kind] ? 'Replace' : 'Upload'}
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {uploading ? <DocumentDialog id={id} kind={uploading} has={documents[uploading]} onClose={() => setUploading(null)} /> : null}
    </Panel>
  );
}

export function RecordsPanel({ id, records, canEdit, canOpen }: { id: string; records: (RecordRef & { createdAt: string })[]; canEdit: boolean; canOpen: boolean }) {
  const [editing, setEditing] = useState<RecordRef | 'new' | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const remove = useRemove(id);
  return (
    <Panel eyebrow="Records" title="Notes & records" action={canEdit ? <Button size="sm" variant="ghost" icon="plus" onClick={() => setEditing('new')}>Add</Button> : undefined}>
      {records.length ? (
        <ul className="list-plain">
          {records.map((r) => (
            <li key={r.id} className="mini-row" style={{ alignItems: 'start' }}>
              <span className="chip chip--plain">{r.kind}</span>
              <div style={{ minWidth: 0 }}>
                <div>{r.description || <span className="faint">No notes</span>}</div>
                <div className="faint mono" style={{ fontSize: 11 }}>
                  {r.fromDate ? `${date(r.fromDate)}${r.toDate ? ` → ${date(r.toDate)}` : ''}` : `Added ${date(r.createdAt)}`}
                </div>
              </div>
              <span className="mini-row__aside row row--wrap" style={{ gap: 4, justifyContent: 'flex-end' }}>
                {r.hasDocument && canOpen ? openLink(`/api/people/${id}/records/${r.id}/document`) : r.hasDocument ? <span className="chip chip--plain">File on record</span> : null}
                {canEdit && removing === r.id ? (
                  <>
                    <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(`records/${r.id}`, { onSettled: () => setRemoving(null) })}>
                      Remove
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(null)}>
                      Keep
                    </Button>
                  </>
                ) : canEdit ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" iconOnly icon="x" aria-label={`Remove ${r.kind}`} onClick={() => setRemoving(r.id)} />
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="faint">No records yet.{canEdit ? ' Add signed statements, warning letters (SP) and forms here, with their scans.' : ''}</p>
      )}
      {editing ? <RecordDialog id={id} record={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Panel>
  );
}

export interface Account {
  email: string;
  status: string;
  lastLoginAt: string | null;
  linkByEmail: boolean;
  locked: boolean;
  superadmin: boolean;
  roles: { app: string; name: string }[];
  /** You hold all of their access: you may change their sign-in and private e-mail, and be handed a link. */
  manageable: boolean;
}

/** HR's view of someone's sign-in (legacy "update login"): the e-mail, whether they may sign in, a password link. */
export function SignInPanel({ id, name, account, hasLeft, canEdit }: { id: string; name: string; account: Account | null; hasLeft: boolean; canEdit: boolean }) {
  const me = useMe().data!;
  const [editing, setEditing] = useState(false);
  const [setupLink, setSetupLink] = useState<string | null>(null);
  const invite = useInvite(id, setSetupLink);
  const unlock = useUnlock(id);
  // Only within your own access (a superadmin's only by a superadmin); a link e-mailed to them is always fine.
  const mayChange = canEdit && !!account && account.manageable;
  const mayLink = canEdit && !!account && (account.manageable || account.linkByEmail);
  return (
    <Panel
      eyebrow="Account"
      title="Sign-in"
      action={mayChange ? <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Change</Button> : undefined}
    >
      {!account ? (
        <p className="faint">No Hearth account.</p>
      ) : (
        <>
          <dl className="dl">
            <dt>Signs in with</dt>
            <dd>{account.email}</dd>
            <dt>Access</dt>
            <dd>
              {hasLeft ? (
                <Chip>Left: can’t sign in</Chip>
              ) : account.status === 'blocked' ? (
                <Chip tone="bad">Blocked</Chip>
              ) : account.locked ? (
                <Chip tone="warn">Locked after wrong passwords</Chip>
              ) : (
                <Chip tone="ok">May sign in</Chip>
              )}
            </dd>
            <dt>Last sign-in</dt>
            <dd>{account.lastLoginAt ? relative(account.lastLoginAt) : 'Never'}</dd>
            <dt>Roles</dt>
            <dd>
              {account.superadmin ? <span className="chip chip--info" style={{ marginRight: 4 }}>Superadmin</span> : null}
              {account.roles.length ? account.roles.map((r) => <span key={`${r.app}:${r.name}`} className="chip chip--plain" style={{ marginRight: 4, marginBottom: 4 }}>{r.name}</span>) : account.superadmin ? null : <span className="faint">Employee only</span>}
            </dd>
          </dl>
          {mayLink ? (
            <div className="row row--wrap" style={{ gap: 6, marginTop: 'var(--s-4)' }}>
              <Button size="sm" icon="key" loading={invite.isPending} disabled={hasLeft || account.status === 'blocked'} onClick={() => invite.mutate()}>
                {account.linkByEmail ? 'Send a password link' : 'Password link'}
              </Button>
              {account.locked && mayChange ? (
                <Button size="sm" variant="ghost" icon="lock" loading={unlock.isPending} onClick={() => unlock.mutate()}>
                  Unlock
                </Button>
              ) : null}
              {me.access.access ? (
                <Link to="/access" className="btn btn--sm btn--ghost">
                  <Icon name="shield" />
                  Roles in Access
                </Link>
              ) : null}
            </div>
          ) : null}
          {mayLink ? <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 10 }}>The link lets them choose a new password; Hearth never sets one for them.{me.access.access ? '' : ' Roles are given under Access, by its administrators.'}</p> : null}
          {canEdit && !account.manageable ? <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 10 }}>They have access you don’t, so you can’t change their sign-in or private e-mail{account.linkByEmail ? '' : ', or be handed a password link for them'}. Someone who holds that access, such as a superadmin, can.</p> : null}
        </>
      )}
      {editing && account ? <AccountDialog id={id} name={name} account={account} onClose={() => setEditing(false)} /> : null}
      <SetupLinkDialog link={setupLink} name={name} onClose={() => setSetupLink(null)} />
    </Panel>
  );
}
